/**
 * Firebase Categories Service
 * Handles Firestore category operations
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage, COLLECTIONS } from '@/config/firebase.config';
import { ProductCategory } from '@/types/product.types';
import { categories as staticCategories, CategoryData } from '@/data/categories';

/**
 * Firebase category document type (extends CategoryData with Firebase-specific fields)
 */
export interface FirebaseCategoryData extends Omit<CategoryData, 'productCount'> {
  isActive: boolean;
  order: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Category form data for create/update operations
 */
export interface CategoryFormData {
  id: string;
  name: string;
  slug: string;
  description: string;
  image: string;
  icon: string;
  color: string;
  isActive: boolean;
  order: number;
}

/**
 * Generate a URL-friendly slug from category name
 */
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Get all categories from Firebase
 */
export const getAllCategories = async (): Promise<CategoryData[]> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.CATEGORIES),
      orderBy('order', 'asc')
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Return static categories if none in Firebase yet
      return staticCategories;
    }

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      docId: doc.id,
      productCount: 0,
    })) as (CategoryData & { docId: string })[];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return staticCategories;
  }
};

/**
 * Get active categories only
 */
export const getActiveCategories = async (): Promise<CategoryData[]> => {
  try {
    const allCategories = await getAllCategories();
    return allCategories.filter(c => c.isActive);
  } catch (error) {
    console.error('Error fetching active categories:', error);
    return staticCategories;
  }
};

/**
 * Get category by document ID
 */
export const getCategoryById = async (categoryId: string): Promise<CategoryData | null> => {
  try {
    const categoryDoc = await getDoc(doc(db, COLLECTIONS.CATEGORIES, categoryId));

    if (!categoryDoc.exists()) {
      return null;
    }

    return {
      ...categoryDoc.data(),
      docId: categoryDoc.id,
      productCount: 0,
    } as CategoryData & { docId: string };
  } catch (error) {
    console.error('Error fetching category:', error);
    throw new Error('Failed to fetch category');
  }
};

/**
 * Get category by slug
 */
export const getCategoryBySlug = async (slug: string): Promise<CategoryData | null> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.CATEGORIES),
      where('slug', '==', slug),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Try static categories as fallback
      const staticCategory = staticCategories.find(c => c.slug === slug);
      return staticCategory || null;
    }

    const docData = snapshot.docs[0];
    return {
      ...docData.data(),
      docId: docData.id,
      productCount: 0,
    } as CategoryData & { docId: string };
  } catch (error) {
    console.error('Error fetching category by slug:', error);
    throw new Error('Failed to fetch category');
  }
};

/**
 * Get category by ProductCategory enum ID
 */
export const getCategoryByEnumId = async (id: ProductCategory): Promise<CategoryData | null> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.CATEGORIES),
      where('id', '==', id),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Try static categories as fallback
      const staticCategory = staticCategories.find(c => c.id === id);
      return staticCategory || null;
    }

    const docData = snapshot.docs[0];
    return {
      ...docData.data(),
      docId: docData.id,
      productCount: 0,
    } as CategoryData & { docId: string };
  } catch (error) {
    console.error('Error fetching category by enum ID:', error);
    throw new Error('Failed to fetch category');
  }
};

/**
 * Create new category
 */
export const createCategory = async (formData: CategoryFormData): Promise<string> => {
  try {
    const now = Timestamp.now();
    const slug = formData.slug || generateSlug(formData.name);

    const categoryData: FirebaseCategoryData = {
      id: formData.id,
      name: formData.name,
      slug,
      description: formData.description,
      image: formData.image,
      icon: formData.icon,
      color: formData.color,
      isActive: formData.isActive,
      order: formData.order,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.CATEGORIES), categoryData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating category:', error);
    throw new Error('Failed to create category');
  }
};

/**
 * Update existing category
 */
export const updateCategory = async (
  docId: string,
  formData: Partial<CategoryFormData>
): Promise<void> => {
  try {
    const updates: Partial<FirebaseCategoryData> & { updatedAt: Timestamp } = {
      updatedAt: Timestamp.now(),
    };

    if (formData.name !== undefined) {
      updates.name = formData.name;
      updates.slug = generateSlug(formData.name);
    }
    if (formData.slug !== undefined) {
      updates.slug = formData.slug;
    }
    if (formData.description !== undefined) {
      updates.description = formData.description;
    }
    if (formData.image !== undefined) {
      updates.image = formData.image;
    }
    if (formData.icon !== undefined) {
      updates.icon = formData.icon;
    }
    if (formData.color !== undefined) {
      updates.color = formData.color;
    }
    if (formData.isActive !== undefined) {
      updates.isActive = formData.isActive;
    }
    if (formData.order !== undefined) {
      updates.order = formData.order;
    }

    await updateDoc(doc(db, COLLECTIONS.CATEGORIES, docId), updates);
  } catch (error) {
    console.error('Error updating category:', error);
    throw new Error('Failed to update category');
  }
};

/**
 * Delete category
 */
export const deleteCategory = async (docId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.CATEGORIES, docId));
  } catch (error) {
    console.error('Error deleting category:', error);
    throw new Error('Failed to delete category');
  }
};

/**
 * Get categories count
 */
export const getCategoriesCount = async (): Promise<number> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.CATEGORIES));
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching categories count:', error);
    return 0;
  }
};

/**
 * Migrate all static categories to Firebase
 */
export const migrateCategories = async (): Promise<number> => {
  try {
    let migratedCount = 0;

    for (let i = 0; i < staticCategories.length; i++) {
      const category = staticCategories[i];
      const now = Timestamp.now();

      const categoryData: FirebaseCategoryData = {
        id: category.id,
        name: category.name,
        slug: category.slug,
        description: category.description,
        image: category.image,
        icon: category.icon,
        color: category.color,
        isActive: true,
        order: i + 1,
        createdAt: now,
        updatedAt: now,
      };

      await addDoc(collection(db, COLLECTIONS.CATEGORIES), categoryData);
      migratedCount++;
    }

    return migratedCount;
  } catch (error) {
    console.error('Error migrating categories:', error);
    throw new Error('Failed to migrate categories');
  }
};

/**
 * Delete all categories from Firebase
 */
export const deleteAllCategories = async (): Promise<number> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.CATEGORIES));
    
    const deletePromises = snapshot.docs.map(docSnapshot => 
      deleteDoc(docSnapshot.ref)
    );
    
    await Promise.all(deletePromises);
    return snapshot.size;
  } catch (error) {
    console.error('Error deleting all categories:', error);
    throw new Error('Failed to delete all categories');
  }
};

/**
 * Upload category image to Firebase Storage
 */
export const uploadCategoryImage = async (
  file: File,
  categoryName: string
): Promise<string> => {
  try {
    const ext = file.name.split('.').pop() || 'png';
    const safeName = categoryName.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').toLowerCase();
    const fileName = `category_${safeName}.${ext}`;
    const storageRef = ref(storage, `categories/${fileName}`);

    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);

    return downloadUrl;
  } catch (error) {
    console.error('Error uploading category image:', error);
    throw new Error('Failed to upload category image');
  }
};

/**
 * Delete category image from Firebase Storage
 */
export const deleteCategoryImage = async (imageUrl: string): Promise<void> => {
  try {
    const storageRef = ref(storage, imageUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting category image:', error);
  }
};
